import React from "react";
import {
  Mail,
  Instagram,
  ArrowUpRight,
  CheckCircle2,
  Mic,
  Handshake,
  UtensilsCrossed,
  Users,
  Sparkles,
  Award,
  BookOpen,
  Film,
  Megaphone,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import Reveal from "../components/Reveal";
import { BRAND, MEMBER_BENEFITS, CAFC_PARTNERSHIP, CAFC_PAST_GUESTS, COMMITTEES, FAQS } from "../mock";

const ROLE_ICONS = { mic: Mic, film: Film };

const FELLOW_PERKS = [
  { icon: Award, title: "Bridge Discussion Lead", body: "An elevated leadership title reserved for our most distinguished Fellows — lead moderated dialogues at BridgeColumbia's flagship events.", elevated: true },
  { icon: Handshake, title: "Private Networking", body: "Off-the-record networking with alumni and civic-dialogue leaders." },
  { icon: UtensilsCrossed, title: "Private Dinners", body: "Small-group dinners with faculty, speakers, and invited guests." },
  { icon: Users, title: "Private Speaker Events", body: "Access to closed speaker sessions reserved for Fellows." },
];

export default function GetInvolved() {
  return (
    <div>
      <section className="relative pt-16 sm:pt-24 pb-8 sm:pb-14 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">Get Involved</p>
          <h1 className="font-serif-display text-[56px] md:text-[96px] font-bold leading-[0.98] text-white tracking-tight">
            Two ways to <span className="hl-block-deep">lead.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-slate-200 font-serif-body text-lg leading-relaxed">
            Ready to go deeper with BridgeColumbia? Apply to the Fellowship for our year-long leadership cohort, or join the Communications Committee to help tell our story.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#fellowship" className="inline-flex items-center gap-2 rounded-full border border-cyan-300/60 hover:border-cyan-200 bg-cyan-300/5 hover:bg-cyan-300/10 px-4 py-2 text-sm text-slate-100 transition-colors">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" /> Fellowship
            </a>
            <a href="#committee" className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-cyan-300/60 hover:bg-cyan-300/5 px-4 py-2 text-sm text-slate-100 transition-colors">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300/60" /> Committee
            </a>
          </div>
        </div>
      </section>

      {/* FELLOWSHIP — dedicated section */}
      <section id="fellowship" className="pt-8 sm:pt-14 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-8">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90">Selective</p>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/50 bg-cyan-300/10 px-3 py-1 text-[11px] tracking-[0.22em] uppercase text-cyan-100">
                <Sparkles className="w-3 h-3" /> Applications open now
              </span>
            </div>
            <h2 className="font-serif-display text-5xl md:text-6xl font-semibold text-white leading-[1.02]">
              The <span className="hl-block-deep">Fellows Program.</span>
            </h2>
            <p className="mt-6 max-w-3xl text-slate-200 font-serif-body text-lg leading-relaxed">
              An exclusive, year-long leadership experience for students committed to dialogue, leadership, and public service — built in partnership with the Columbia Academic Freedom Council.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CAFC PARTNERSHIP CALLOUT — part of Fellowship track */}
      <section className="pt-2 sm:pt-4">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-cyan-300/30 bg-gradient-to-br from-[#196d9c] to-[#0a3d5e] p-8 md:p-12">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-cyan-300/10 blur-3xl" />
              <div className="relative grid md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/50 bg-cyan-300/10 px-3 py-1 text-[11px] tracking-[0.22em] uppercase text-cyan-100 mb-5">
                    <BookOpen className="w-3.5 h-3.5" /> Featured Partnership
                  </div>
                  <h2 className="font-serif-display text-3xl md:text-4xl font-semibold text-white leading-tight">
                    {CAFC_PARTNERSHIP.title}
                  </h2>
                </div>
                <div className="md:col-span-7">
                  <p className="text-slate-100 font-serif-body text-lg leading-relaxed">
                    {CAFC_PARTNERSHIP.body}
                  </p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                    {CAFC_PARTNERSHIP.perks.map((p, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-slate-100 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-cyan-200 mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Past notable events */}
                  <div className="mt-8 pt-6 border-t border-white/15">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-200/90 mb-4">
                      Notable events
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {CAFC_PAST_GUESTS.map((g, i) => {
                        const inner = (
                          <>
                            <p className="font-serif-display text-lg text-white leading-tight">{g.name}</p>
                            <p className="mt-1 text-xs uppercase tracking-[0.16em] text-cyan-200/90">{g.role}</p>
                            <p className="mt-2 text-sm text-slate-300 font-serif-body leading-relaxed">{g.note}</p>
                            {g.url && (
                              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-200 group-hover:text-cyan-100">
                                Read more <ArrowUpRight className="w-3 h-3" />
                              </p>
                            )}
                          </>
                        );
                        const cls =
                          "group block rounded-xl border border-white/15 bg-[#0a3d5e]/60 p-4 hover:border-cyan-300/40 transition-colors";
                        return g.url ? (
                          <a key={i} href={g.url} target="_blank" rel="noreferrer" className={cls}>
                            {inner}
                          </a>
                        ) : (
                          <div key={i} className={cls}>
                            {inner}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Fellowship perks + apply CTA */}
      <section className="py-8 sm:py-14">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">What Fellows Get</p>
              <h2 className="font-serif-display text-4xl md:text-5xl font-semibold text-white leading-tight">
                A year of <span className="hl-block-deep">leadership + access.</span>
              </h2>
            </Reveal>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {FELLOW_PERKS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Reveal key={i} delay={i * 80}>
                    <div className={`relative rounded-2xl border p-6 transition-colors h-full ${
                      p.elevated
                        ? "border-cyan-300/60 bg-gradient-to-br from-[#1a618d] to-[#0a3d5e] hover:border-cyan-200"
                        : "border-white/15 bg-[#145074] hover:border-cyan-300/50 hover:bg-[#1a618d]"
                    }`}>
                      {p.elevated && (
                        <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full border border-cyan-300/60 bg-cyan-300/15 px-2.5 py-0.5 text-[10px] tracking-[0.16em] uppercase text-cyan-100">
                          <Sparkles className="w-3 h-3" /> Distinguished
                        </span>
                      )}
                      <div className="w-11 h-11 rounded-xl bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-cyan-200" />
                      </div>
                      <h3 className="mt-5 font-serif-display text-xl text-white">{p.title}</h3>
                      <p className="mt-2 text-slate-200 text-sm font-serif-body leading-relaxed">{p.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={200}>
              <div className="mt-6 rounded-2xl border border-white/15 bg-[#093349] p-6 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center">
                  <Award className="w-5 h-5 text-cyan-200" />
                </div>
                <div>
                  <p className="font-serif-display text-lg text-white">Priority for board roles</p>
                  <p className="mt-1.5 text-slate-300 font-serif-body text-sm leading-relaxed">
                    Fellows get first consideration when board positions open. Board members represent the chapter at the annual <span className="text-cyan-200">BridgeUSA National Summit</span> and lead the coalition year-round.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={280}>
              <ul className="mt-6 space-y-2.5">
                {MEMBER_BENEFITS.slice(4).map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-200 font-serif-body">
                    <CheckCircle2 className="w-5 h-5 text-cyan-200 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={100}>
              <div className="sticky top-28 rounded-3xl border border-cyan-300/30 bg-gradient-to-br from-[#196d9c] to-[#0a3d5e] p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">Apply</p>
                <h3 className="font-serif-display text-3xl md:text-4xl font-semibold text-white leading-tight">
                  Ready to become a <span className="hl-block-deep">Fellow?</span>
                </h3>
                <p className="mt-5 text-slate-200 font-serif-body">
                  Applications for the incoming class of BridgeColumbia Fellows are now open. Submit the form below to apply.
                </p>
                {BRAND.fellowshipUrl ? (
                  <a
                    href={BRAND.fellowshipUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-cyan-300 hover:bg-cyan-200 text-[#062a40] px-6 py-3 font-medium transition-colors btn-shine"
                  >
                    Apply for the Fellowship <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-cyan-300/40 text-[#062a40]/70 px-6 py-3 font-medium cursor-not-allowed"
                  >
                    Application form coming soon
                  </button>
                )}
                <p className="mt-4 text-xs text-cyan-100/70">
                  Follow us on Instagram to be first to know when applications open.
                </p>

                <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="group flex items-center gap-3 text-slate-200 hover:text-cyan-200 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{BRAND.email}</span>
                  </a>
                  <a
                    href={BRAND.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center gap-3 text-slate-200 hover:text-cyan-200 transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                    <span className="text-sm">{BRAND.instagramHandle}</span>
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Committee Applications */}
      <section id="committee" className="py-10 sm:py-16 border-t border-white/10 bg-[#093349] scroll-mt-24 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90">Also open</p>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/50 bg-cyan-300/10 px-3 py-1 text-[11px] tracking-[0.22em] uppercase text-cyan-100">
                <Megaphone className="w-3 h-3" /> Committee applications
              </span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl font-semibold text-white leading-[1.02] break-words">
              Join our <span className="hl-block-deep">Communications Committee.</span>
            </h2>
          </Reveal>

          {COMMITTEES.map((c, ci) => (
            <div key={ci} className="mt-10">
              <Reveal>
                <p className="text-slate-200 font-serif-body text-lg leading-relaxed max-w-3xl">
                  {c.body}
                </p>
              </Reveal>

              <div className="mt-8 grid md:grid-cols-2 gap-5">
                {c.roles.map((r, i) => {
                  const Icon = ROLE_ICONS[r.icon] || Users;
                  return (
                    <Reveal key={i} delay={i * 80}>
                      <article className="group rounded-2xl border border-white/15 bg-[#145074] p-7 hover:border-cyan-300/60 hover:bg-[#1a618d] transition-colors h-full">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-cyan-200" />
                          </div>
                          <p className="text-[11px] uppercase tracking-[0.22em] text-cyan-200">
                            {c.name}
                          </p>
                        </div>
                        <h3 className="mt-5 font-serif-display text-2xl md:text-3xl text-white leading-tight">
                          {r.title}
                        </h3>
                        <p className="mt-3 text-slate-200 font-serif-body leading-relaxed">
                          {r.body}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          {r.skills.map((s, j) => (
                            <span
                              key={j}
                              className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] tracking-wide text-cyan-100"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </article>
                    </Reveal>
                  );
                })}
              </div>

              <Reveal delay={200}>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <a
                    href={c.applyUrl || BRAND.committeeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-cyan-300 hover:bg-cyan-200 text-[#062a40] px-6 py-3 font-medium transition-colors btn-shine"
                  >
                    Apply to the Committee <ArrowUpRight className="w-4 h-4" />
                  </a>
                  <p className="text-sm text-slate-300">
                    Follow us on Instagram for updates and open calls.
                  </p>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 sm:py-20 border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">Questions</p>
            <h2 className="font-serif-display text-5xl md:text-6xl font-semibold text-white leading-[1.02]">
              Frequently <span className="hl-block-deep">asked.</span>
            </h2>
            <p className="mt-5 text-slate-200 font-serif-body text-lg max-w-2xl">
              Everything you might want to know before joining, co-hosting, or showing up to your first discussion.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <Accordion type="single" collapsible className="mt-10 rounded-2xl border border-white/15 bg-[#145074] divide-y divide-white/10">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-0 px-6 md:px-8">
                  <AccordionTrigger className="py-6 text-lg md:text-xl text-white font-serif-display hover:no-underline [&>svg]:text-cyan-200">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pt-0 text-slate-200 font-serif-body text-base leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
